export type OnboardingPlacement = 'left' | 'right' | 'inside';
export type OnboardingCompletedPanelPlacement = 'below-center';

export type OnboardingAction =
  | 'open-user-menu'
  | 'switch-theme'
  | 'open-session-sale'
  | 'select-one-hour-tariff'
  | 'select-cash-payment'
  | 'complete-one-hour-session'
  | 'open-session-refund'
  | 'complete-session-refund'
  | 'open-template-library'
  | 'apply-shortcut-template'
  | 'enter-shortcut-path'
  | 'complete-template-shortcut'
  | 'select-report-review'
  | 'generate-report-review'
  | 'select-report-sales'
  | 'generate-report-sales'
  | 'select-report-hosts'
  | 'generate-report-hosts';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  target: string;
  secondaryTarget?: string;
  completedSecondaryTarget?: string;
  route?: string;
  placement: OnboardingPlacement;
  panelGap?: number;
  completedPanelPlacement?: OnboardingCompletedPanelPlacement;
  action?: OnboardingAction;
  completedTitle?: string;
  completedDescription?: string;
}

interface OnboardingPage {
  id: string;
  route: string;
  title: string;
  description: string;
}

// Тексты адаптированы для короткого тура по официальной базе знаний SmartShell:
// https://support.smartshell.gg/hc/ru/sections/14533768164365
// https://support.smartshell.gg/hc/ru/articles/10934961046541
// https://support.smartshell.gg/hc/ru/articles/10935056179597
// https://support.smartshell.gg/hc/ru/articles/10935200920077
const ONBOARDING_PAGES: readonly OnboardingPage[] = [
  {
    id: 'dashboard',
    route: '/',
    title: 'Дашборд',
    description:
      'Здесь собраны показатели текущей смены: выручка, активные пользователи, занятые компьютеры, продажи и задачи операторов.',
  },
  {
    id: 'computers',
    route: '/computers',
    title: 'Компьютеры',
    description:
      'Таблица показывает состояние каждого хоста, активный сеанс, тариф, время окончания и остаток. Нажмите на хост или никнейм, чтобы открыть подробную карточку.',
  },
  {
    id: 'map',
    route: '/map',
    title: 'Карта клуба',
    description:
      'Карта повторяет реальную расстановку игровых мест. С неё удобно видеть загрузку зон, выбирать хосты и выполнять групповые действия.',
  },
  {
    id: 'bookings',
    route: '/bookings',
    title: 'Бронирования',
    description:
      'Расписание показывает хосты по вертикали и время по горизонтали. Выберите дату, группу и свободный интервал, чтобы создать бронь.',
  },
  {
    id: 'shop',
    route: '/shop',
    title: 'Магазин',
    description:
      'Рабочее место оператора для продажи тарифов, товаров, услуг и комбо. Здесь же выбирается клиент и способ оплаты.',
  },
  {
    id: 'payments',
    route: '/payments',
    title: 'Платежи',
    description:
      'История операций текущей смены помогает проверить состав покупки, способ оплаты, сотрудника и при необходимости оформить возврат.',
  },
  {
    id: 'clients',
    route: '/clients',
    title: 'Клиенты',
    description:
      'В списке сразу видны баланс, скидка, пополнения и основные данные гостя. Поиск найдёт нужного человека, а карточка покажет его историю и доступные действия.',
  },
  {
    id: 'logs',
    route: '/logs',
    title: 'Логи',
    description:
      'Журнал фиксирует действия сотрудников и изменения в клубе. Используйте фильтры и поиск, чтобы быстро восстановить последовательность событий.',
  },
  {
    id: 'tariffs',
    route: '/tariffs',
    title: 'Тарифы',
    description:
      'Тариф решает, сколько времени получит гость, сколько заплатит и в каких зонах сможет играть. Добавьте расписание — и один тариф легко адаптируется к будням и выходным.',
  },
  {
    id: 'goods',
    route: '/goods',
    title: 'Товары',
    description:
      'Товары связаны со складом: здесь создают карточки, меняют цены, принимают поставки и списывают остатки. Так оператор всегда знает, что можно продать гостю прямо сейчас.',
  },
  {
    id: 'services',
    route: '/services',
    title: 'Услуги',
    description:
      'Услуги продаются через магазин, но не требуют складского остатка. В карточке задаются название, категория и цена.',
  },
  {
    id: 'combos',
    route: '/combos',
    title: 'Комбо-наборы',
    description:
      'Комбо объединяют несколько позиций в одно предложение. Это удобно для готовых наборов из игрового времени, товаров и услуг.',
  },
  {
    id: 'discounts',
    route: '/discounts',
    title: 'Скидки',
    description:
      'Скидка снижает цену выбранных тарифов, товаров или услуг. Для неё задаются процент, период действия и доступные категории.',
  },
  {
    id: 'promo-codes',
    route: '/promo-codes',
    title: 'Промокоды',
    description:
      'Промокоды используются для маркетинговых акций: они могут давать скидку, пополнять депозит или начислять бонусы.',
  },
  {
    id: 'cashback',
    route: '/cashback',
    title: 'Кешбэк',
    description:
      'Правила кешбэка начисляют клиенту бонус при пополнении на заданную сумму. Размер может быть фиксированным или процентным.',
  },
  {
    id: 'achievements',
    route: '/achievements',
    title: 'Достижения',
    description:
      'Достижения награждают клиентов за регистрацию, пополнения, траты и игровое время, помогая выстраивать программу лояльности.',
  },
  {
    id: 'workers',
    route: '/workers',
    title: 'Сотрудники',
    description:
      'Здесь управляют командой и ролями. Владелец, менеджер и операторы получают разные права на чувствительные и повседневные операции.',
  },
  {
    id: 'reviews',
    route: '/reviews',
    title: 'Отзывы',
    description:
      'Отзывы помогают следить за впечатлениями гостей. Откройте карточку, прочитайте комментарий и отметьте его обработанным.',
  },
  {
    id: 'game-accounts',
    route: '/game-accounts',
    title: 'Клубные аккаунты',
    description:
      'Пулы клубных аккаунтов используются играми и приложениями. Здесь видно доступность учётных записей и их распределение.',
  },
  {
    id: 'applications',
    route: '/applications?type=GAME',
    title: 'Игры и приложения',
    description:
      'Создавайте ярлыки вручную или из шаблонов. Шаблон заполняет название, обложку, описание и скриншоты, а группы помогают организовать библиотеку.',
  },
  {
    id: 'news',
    route: '/news',
    title: 'Новости',
    description:
      'Новости клуба видят пользователи клиентской оболочки. Здесь можно подготовить публикацию, обложку и текст анонса.',
  },
  {
    id: 'reports',
    route: '/reports',
    title: 'Аналитика',
    description:
      'Выберите тип отчёта и период, затем нажмите «Сгенерировать». Смены, продажи, посетители и загрузка клуба рассчитываются согласованно для выбранных дат.',
  },
  {
    id: 'advertisement',
    route: '/advertisement',
    title: 'Объявления',
    description:
      'Это доска объявлений для клубов. Здесь продают оборудование, мебель и готовые бизнесы, а ещё предлагают дизайн, маркетинг и другие услуги коллегам по индустрии.',
  },
  {
    id: 'settings',
    route: '/settings',
    title: 'Настройки',
    description:
      'Параметры клуба собраны по тематическим вкладкам. Меняйте только нужный блок и проверяйте влияние настройки на рабочий сценарий.',
  },
];

export const ONBOARDING_PAGE_ROUTES = ONBOARDING_PAGES.map(
  ({ route }) => route
);

const PAGE_TARGET = '[data-onboarding="page-content"]';
const ACTIVE_MENU_TARGET =
  '[aria-current="page"], [data-onboarding-menu-active="true"]';

export const ONBOARDING_ONE_HOUR_TARIFF_TITLE = '1 час Общий зал';

const SESSION_SCENARIO_STEPS: readonly OnboardingStep[] = [
  {
    id: 'session-open-sale',
    route: '/computers',
    title: 'Поставьте сеанс на свободный хост',
    description:
      'Дважды нажмите на подсвеченный свободный компьютер. Откроется окно продажи для выбранного хоста.',
    target: '[data-onboarding="free-host"]',
    placement: 'right',
    action: 'open-session-sale',
  },
  {
    id: 'session-select-tariff',
    route: '/computers',
    title: 'Выберите тариф на один час',
    description: `Добавьте в корзину тариф «${ONBOARDING_ONE_HOUR_TARIFF_TITLE}». Время окончания рассчитается автоматически.`,
    target: '[data-onboarding="start-session-tariffs"]',
    placement: 'left',
    panelGap: 0,
    action: 'select-one-hour-tariff',
  },
  {
    id: 'session-review-cart',
    route: '/computers',
    title: 'Тариф добавлен в корзину',
    description:
      'Выбранный тариф появился в корзине. Здесь можно проверить состав покупки, скидку и рассчитанное время окончания сеанса.',
    target: '[data-onboarding="start-session-cart"]',
    placement: 'left',
  },
  {
    id: 'session-select-payment',
    route: '/computers',
    title: 'Выберите способ оплаты',
    description:
      'Для учебного сценария выберите «Наличные». После этого останется подтвердить оплату и запустить сеанс.',
    target: '[data-onboarding="start-session-payment"]',
    placement: 'left',
    action: 'select-cash-payment',
  },
  {
    id: 'session-complete',
    route: '/computers',
    title: 'Запустите сеанс',
    description:
      'Нажмите «Оплатить». После успешной операции хост станет занятым, а остаток времени начнёт уменьшаться каждую секунду.',
    target: '[data-onboarding="start-session-payment"]',
    placement: 'left',
    action: 'complete-one-hour-session',
    completedPanelPlacement: 'below-center',
    completedTitle: 'Сеанс запущен',
    completedDescription:
      'Хост стал занятым. В строке появились гость, тариф, время окончания и посекундный остаток.',
  },
];

const PAYMENT_SCENARIO_STEPS: readonly OnboardingStep[] = [
  {
    id: 'payment-open-refund',
    route: '/payments',
    title: 'Отмените созданный платеж',
    description:
      'Найдите платеж за часовой сеанс и нажмите «Отменить». Отмена платежа завершит связанный сеанс и снова освободит компьютер.',
    target: '[data-onboarding="payment-refund"]',
    placement: 'left',
    action: 'open-session-refund',
  },
  {
    id: 'payment-confirm-refund',
    route: '/payments',
    title: 'Подтвердите отмену',
    description:
      'Нажмите «Подтвердить». Платеж получит статус отмены, а хост снова освободится.',
    target: '[data-onboarding="refund-confirm"]',
    placement: 'right',
    action: 'complete-session-refund',
    completedTitle: 'Платеж отменен',
    completedDescription:
      'Платеж и связанный сеанс обновились согласованно. Компьютер снова готов принять гостя.',
  },
];

const SHORTCUT_SCENARIO_STEPS: readonly OnboardingStep[] = [
  {
    id: 'shortcut-open-library',
    route: '/applications/manage?type=GAME',
    title: 'Создайте игру из шаблона',
    description:
      'Откройте библиотеку кнопкой «Шаблоны». В ней уже подготовлены настоящие обложки, описания и скриншоты игр.',
    target: '[data-onboarding="shortcut-template-controls"]',
    placement: 'right',
    action: 'open-template-library',
  },
  {
    id: 'shortcut-apply-template',
    route: '/applications/manage?type=GAME',
    title: 'Примените шаблон',
    description:
      'Выберите любую игру и нажмите кнопку с галочкой. Поля нового ярлыка заполнятся данными шаблона.',
    target: '[data-onboarding="shortcut-template-library"]',
    placement: 'left',
    action: 'apply-shortcut-template',
  },
  {
    id: 'shortcut-enter-path',
    route: '/applications/manage?type=GAME',
    title: 'Укажите путь к игре',
    description:
      'Шаблон заполняет карточку, но путь зависит от установки игры в клубе. Введите, например, C:\\Games\\Demo\\game.exe в первое поле пути.',
    target: '[data-onboarding="shortcut-path"]',
    placement: 'right',
    action: 'enter-shortcut-path',
  },
  {
    id: 'shortcut-complete',
    title: 'Создайте ярлык',
    description:
      'Проверьте заполненные поля и нажмите «Создать». Новый ярлык сразу появится в библиотеке игр.',
    target: '[data-onboarding="shortcut-create"]',
    placement: 'right',
    action: 'complete-template-shortcut',
    completedTitle: 'Ярлык создан',
    completedDescription:
      'Новая карточка уже в группе игр. Можно открыть её и проверить обложку, описание и скриншоты.',
  },
];

const REPORT_SCENARIO_STEPS: readonly OnboardingStep[] = [
  {
    id: 'reports-select-review',
    route: '/reports',
    title: 'Откройте обзорный отчет',
    description:
      'Переключитесь на вкладку «Обзорный». Она объединяет финансовые и операционные показатели клуба.',
    target: '[data-onboarding="reports-tabs"]',
    placement: 'right',
    action: 'select-report-review',
  },
  {
    id: 'reports-generate-review',
    route: '/reports',
    title: 'Сгенерируйте обзорный отчет',
    description:
      'Нажмите «Сгенерировать». Показатели, диаграммы и сводки будут рассчитаны для выбранного периода.',
    target: '[data-onboarding="report-review-generate"]',
    completedSecondaryTarget: '[data-onboarding="report-review-result"]',
    placement: 'left',
    action: 'generate-report-review',
  },
  {
    id: 'reports-select-sales',
    route: '/reports',
    title: 'Перейдите в отчет по продажам',
    description:
      'Выберите вкладку «Продажи», чтобы посмотреть выручку и количество продаж по категориям.',
    target: '[data-onboarding="reports-tabs"]',
    placement: 'right',
    action: 'select-report-sales',
  },
  {
    id: 'reports-generate-sales',
    route: '/reports',
    title: 'Сгенерируйте отчет по продажам',
    description:
      'Нажмите «Сгенерировать». Итоги и таблица будут построены на общем наборе данных выбранного периода.',
    target: '[data-onboarding="report-sales-generate"]',
    completedSecondaryTarget: '[data-onboarding="report-sales-result"]',
    placement: 'left',
    action: 'generate-report-sales',
  },
  {
    id: 'reports-select-hosts',
    route: '/reports',
    title: 'Откройте занятость оборудования',
    description:
      'Выберите вкладку «Занятость оборудования». Здесь сравниваются загрузка хостов, число сеансов и выручка.',
    target: '[data-onboarding="reports-tabs"]',
    placement: 'right',
    action: 'select-report-hosts',
  },
  {
    id: 'reports-generate-hosts',
    route: '/reports',
    title: 'Сгенерируйте отчет по занятости',
    description:
      'Нажмите «Сгенерировать», чтобы получить согласованные значения по каждому хосту и группе.',
    target: '[data-onboarding="report-hosts-generate"]',
    completedSecondaryTarget: '[data-onboarding="report-hosts-result"]',
    placement: 'left',
    action: 'generate-report-hosts',
  },
];

const PAGE_STEPS = ONBOARDING_PAGES.reduce<OnboardingStep[]>((steps, page) => {
  steps.push({
    ...page,
    target: PAGE_TARGET,
    secondaryTarget: ACTIVE_MENU_TARGET,
    placement: 'inside',
  });

  if (page.id === 'computers') {
    steps.push(...SESSION_SCENARIO_STEPS);
  }

  if (page.id === 'applications') {
    steps.push(...SHORTCUT_SCENARIO_STEPS);
  }

  if (page.id === 'payments') {
    steps.push(...PAYMENT_SCENARIO_STEPS);
  }

  if (page.id === 'reports') {
    steps.push(...REPORT_SCENARIO_STEPS);
  }

  return steps;
}, []);

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    id: 'open-user-menu',
    route: '/',
    title: 'Добро пожаловать в SmartShell',
    description:
      'Начнём с оформления интерфейса. Откройте меню сотрудника в правом верхнем углу — там находится переключатель темы.',
    target: '[data-onboarding="user-menu"]',
    placement: 'left',
    action: 'open-user-menu',
  },
  {
    id: 'switch-theme',
    route: '/',
    title: 'Включите тёмную тему',
    description:
      'Переключите тему со светлой на тёмную. Так заодно познакомимся с персональными настройками интерфейса.',
    target: '[data-onboarding="theme-switcher"]',
    placement: 'left',
    action: 'switch-theme',
  },
  {
    id: 'main-navigation',
    route: '/',
    title: 'Основные разделы',
    description:
      'Боковое меню разделяет ежедневную работу оператора, управление клубом, контент, аналитику и настройки. Сейчас последовательно посмотрим каждую верхнеуровневую страницу.',
    target: '[data-onboarding="main-navigation"]',
    placement: 'right',
  },
  ...PAGE_STEPS,
  {
    id: 'complete',
    route: '/',
    title: 'Знакомство завершено',
    description:
      'Готово — теперь SmartShell уже не похож на лабиринт. Исследуйте разделы, запускайте сеансы, собирайте отчёты и возвращайтесь к туру через меню сотрудника, если захочется освежить маршрут.',
    target: PAGE_TARGET,
    placement: 'inside',
  },
];
